import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Check, ChevronDown, ChevronUp } from 'lucide-react';

const AwardDetails = () => {
  const { id } = useParams();
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [expandedSections, setExpandedSections] = useState({});

  const awardCategories = {
    "HIGHER EDUCATION CATEGORIES": [
      {
        id: 1,
        name: "Excellence in AI-Integrated Higher Education",
        description: "Honors higher education institutions that have strategically integrated Artificial Intelligence (AI) across their academic or administrative, or in research ecosystems to enhance learning outcomes, operational efficiency, and innovation.",
        price: 20000
      },
      {
        id: 2,
        name: "Outstanding Future-Ready Higher Education Institution",
        description: "Recognizes a university or institute demonstrating a visionary approach to integrating technology, innovation, and sustainability across academics, research, and campus life.",
        price: 20000
      },
      {
        id: 3,
        name: "Excellence in Next-Gen Digital & Blended Learning",
        description: "Recognizes higher education institutions that have redefined learning through advanced digital, hybrid, and blended pedagogies — integrating AI tools, adaptive platforms, immersive learning environments, and data-driven teaching models",
        price: 20000
      }
    ],
    "SCHOOL (K12) - Schools offering Classes 1–12": [
      {
        id: 4,
        name: "Outstanding K-12 School in Technology Integration",
        description: "Recognizes a school that has demonstrated outstanding innovation and leadership in integrating technology across teaching, learning, and administration.",
        price: 20000
      },
      {
        id: 5,
        name: "Outstanding Technology-Driven School Network (Group/Chain)",
        description: "Recognizes a school group or chain demonstrating exceptional vision and consistency in implementing technology across campuses.",
        price: 20000
      }
    ],
    "EDTECH/ Tech Enabled Enterprise CATEGORIES": [
      {
        id: 6,
        name: "Excellence in Generative AI Innovation for Learning & Assessment",
        description: "Recognizes EdTech companies, platforms, and education enterprises that are pioneering the use of Generative Artificial Intelligence (GenAI) to redefine learning, content creation, and assessment design.",
        price: 20000
      },
      {
        id: 7,
        name: "Excellence in AI-Powered Learning & Data Intelligence Solutions",
        description: "Recognizes EdTech companies, learning platforms, and education solution providers that harness Artificial Intelligence (AI), data analytics, and machine learning to transform how educational insights are captured, analyzed, and applied.",
        price: 20000
      }
    ],
    "LEADERSHIP CATEGORIES": [
      {
        id: 8,
        name: "Pioneering Leader in Technology-Driven School Transformation",
        description: "Celebrates K12 school leaders who are driving transformative change through technology adoption.",
        price: 20000
      },
      {
        id: 9,
        name: "Visionary Leader in Technology-Enabled Higher Education",
        description: "Celebrates higher education leaders who are shaping the future of learning through technology.",
        price: 20000
      }
    ]
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const toggleCategory = (categoryId) => {
    setSelectedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const getTotalCategories = () => {
    return Object.values(awardCategories).flat().length;
  };

  const getSelectedCategoryDetails = () => {
    const allCategories = Object.values(awardCategories).flat();
    return selectedCategories.map(id => allCategories.find(cat => cat.id === id));
  };

  return (
    <div style={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ padding: '40px 24px 0' }}>
        <div className="container-custom">
          <Link to="/awards/upcoming" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: '#667eea', textDecoration: 'none', marginBottom: '30px', fontWeight: '600' }}>
            <ArrowLeft size={20} />
            <span>Back to Awards</span>
          </Link>
          
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <h1 style={{ color: '#2d3748', fontSize: '2.5rem', fontWeight: 700, marginBottom: '10px' }}>TechEdu India Awards 2026</h1>
            <h2 style={{ color: '#667eea', fontSize: '1.5rem', fontWeight: 600 }}>Select Award Category</h2>
            <p style={{ color: '#718096', fontSize: '1rem', marginTop: '10px' }}>You can select one or more categories.</p>
          </div>
        </div>
      </div>

      {/* Categories Selection */}
      <div className="container-custom" style={{ padding: '0 24px 80px' }}>
        <div style={{ marginBottom: '30px', textAlign: 'center' }}>
          <p style={{ color: '#4a5568', fontSize: '1.1rem', fontWeight: 600 }}>
            showing {getTotalCategories()} of {getTotalCategories()}
          </p>
        </div>

        {Object.entries(awardCategories).map(([sectionName, categories]) => (
          <div key={sectionName} style={{ marginBottom: '30px' }}>
            {/* Section Header */}
            <div 
              onClick={() => toggleSection(sectionName)}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                padding: '20px',
                borderRadius: '12px',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '15px'
              }}
            >
              <div>
                <h3 style={{ fontSize: '1.3rem', fontWeight: 700, margin: 0 }}>{sectionName}</h3>
                <p style={{ fontSize: '0.9rem', margin: '5px 0 0 0', opacity: 0.9 }}>{categories.length} Sub Categories</p>
              </div>
              {expandedSections[sectionName] ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
            </div>

            {/* Categories List */}
            {expandedSections[sectionName] && (
              <div style={{ display: 'grid', gap: '20px' }}>
                {categories.map((category) => (
                  <div 
                    key={category.id}
                    onClick={() => toggleCategory(category.id)}
                    style={{
                      backgroundColor: 'white',
                      border: selectedCategories.includes(category.id) ? '3px solid #667eea' : '2px solid #e2e8f0',
                      borderRadius: '12px',
                      padding: '25px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      position: 'relative'
                    }}
                  >
                    {selectedCategories.includes(category.id) && (
                      <div style={{
                        position: 'absolute',
                        top: '15px',
                        right: '15px',
                        width: '30px',
                        height: '30px',
                        backgroundColor: '#667eea',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Check size={18} color="white" />
                      </div>
                    )}
                    
                    <h4 style={{ color: '#2d3748', fontSize: '1.3rem', fontWeight: 700, marginBottom: '15px' }}>
                      {category.name}
                    </h4>
                    <p style={{ color: '#4a5568', fontSize: '1rem', lineHeight: '1.6', marginBottom: '20px' }}>
                      {category.description}
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ color: '#667eea', fontSize: '1.5rem', fontWeight: 700 }}>₹ {category.price.toLocaleString()}</span>
                      <span style={{ color: '#718096', fontSize: '0.9rem' }}>+18% GST</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* Proceed Button */}
        {selectedCategories.length > 0 && (
          <div style={{ 
            position: 'fixed', 
            bottom: '0', 
            left: '0', 
            right: '0', 
            backgroundColor: 'white', 
            padding: '20px', 
            borderTop: '2px solid #e2e8f0',
            boxShadow: '0 -4px 20px rgba(0,0,0,0.1)'
          }}>
            <div className="container-custom" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p style={{ color: '#2d3748', fontSize: '1.1rem', fontWeight: 600, margin: 0 }}>
                  {selectedCategories.length} categories selected
                </p>
                <p style={{ color: '#718096', fontSize: '0.9rem', margin: 0 }}>
                  Total: ₹ {(getSelectedCategoryDetails().reduce((sum, cat) => sum + cat.price, 0)).toLocaleString()} + GST
                </p>
              </div>
              <Link 
                to={`/awards/nomination/${selectedCategories.join(',')}`}
                style={{
                  padding: '15px 40px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  textDecoration: 'none',
                  borderRadius: '25px',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  transition: 'transform 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
              >
                Proceed to Nomination
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AwardDetails;